import { SelfContainedCommand, CommandExecutionContext } from "types/commands";
import { StandardToolResult, createSuccessResult, createErrorResult } from "types/standardToolResults";
import { createFileLogger } from '../../../utils/logger';
import { entityCreationRegistry } from '../../../services/entityCreationRegistry';

const log = createFileLogger('SHEETS_COMMANDS');

export const sheetsCreateCommand: SelfContainedCommand = {
    id: 'sheets_create',
    description: '📊 CREATE: Create a new spreadsheet entity and optionally open it in a window. By default creates a window unless createWindow=false is passed.',
    autonomyLevel: 'auto',
    visualTreatment: 'demonstrative',
    
    schema: {
        parameters: [
            {
                name: 'name',
                type: 'string',
                description: 'Name for the new spreadsheet (default: "Untitled Sheet")',
                required: false,
                defaultValue: 'Untitled Sheet',
                validator: (value: string) => {
                    if (value && value.trim().length === 0) return 'Sheet name cannot be empty';
                    if (value && value.length > 100) return 'Sheet name too long (max 100 chars)';
                    return null;
                },
                examples: ['Budget 2024', 'Contact List', 'Project Timeline']
            },
            {
                name: 'spaceId',
                type: 'string',
                description: 'The ID of the space to create the sheet in. Will use context spaceId if not provided.',
                required: false,
                examples: ['space-123', 'space-abc-def']
            },
            {
                name: 'folderId',
                type: 'string',
                description: 'The ID of the folder to create the sheet in. Leave empty for root level.',
                required: false,
                examples: ['folder-123', null]
            },
            {
                name: 'createWindow',
                type: 'boolean',
                description: 'Whether to create and open a window for the sheet. Default is true.',
                required: false,
                defaultValue: true,
                examples: [true, false]
            }
        ]
    },
    
    examples: {
        good: [
            {
                input: { name: 'Budget 2024' },
                expectedOutput: createSuccessResult('Spreadsheet created successfully', {
                    entityId: 'sheet-123',
                    name: 'Budget 2024',
                    entityType: 'Sheet'
                }),
                description: 'Create a new spreadsheet with a custom name',
                context: 'When starting a new spreadsheet for a specific purpose'
            },
            {
                input: { name: 'Contact List', spaceId: 'space-xyz' },
                expectedOutput: createSuccessResult('Spreadsheet created successfully', {
                    entityId: 'sheet-456',
                    name: 'Contact List',
                    entityType: 'Sheet'
                }),
                description: 'Create a new spreadsheet in a specific space',
                context: 'When organizing spreadsheets across different spaces'
            }
        ],
        bad: [
            {
                input: { name: '' },
                error: 'Sheet name cannot be empty',
                why: 'Empty names are not allowed',
                fix: 'Provide a non-empty name or omit the parameter to use default'
            }
        ]
    },
    
    performance: {
        expectedDuration: 500,
        complexity: 'low'
    },
    
    handler: async (params: any, context: CommandExecutionContext): Promise<StandardToolResult> => {
        const startTime = Date.now();
        const { name = 'Untitled Sheet', spaceId: providedSpaceId, folderId, createWindow = true } = params;
        const spaceId = providedSpaceId || context.spaceId;
        
        try {
            log.log('[sheets_create] Creating new spreadsheet:', { name, spaceId });
            
            console.log('[sheets_create] 🔍 Received folderId parameter:', {
                folderId: folderId,
                folderIdType: typeof folderId,
                isNull: folderId === null,
                isUndefined: folderId === undefined,
                allParams: params
            });
            
            // ✅ ARCHITECTURE: Use entityStore actions for proper store/CRDT sync
            const now = new Date().toISOString();
            const newSheetEntityData = {
                entityType: 'Sheet',
                name: name.trim(),
                spaceId: spaceId,
                skeleton: {
                    '@type': 'Sheet',
                    version: 'v2',
                    architecture: 'unified',
                    groupIds: folderId ? [folderId] : [] // Set groupIds during creation
                },
                created_at: now,
            };
            
            log.log('[sheets_create] Creating spreadsheet via entityStore:', { 
                spaceId: newSheetEntityData.spaceId,
                name: newSheetEntityData.name,
                folderId
            });
            
            // ✅ Use store actions - handles EVERYTHING automatically:
            // - Backend creation
            // - Store updates
            // - CRDT sync
            // - Event emission
            const useEntityStore = (await import('../../../stores/entityStore')).default;
            const newSheet = await useEntityStore.getState().actions.createEntity(newSheetEntityData);
            const entityId = newSheet._id || newSheet.id;
            const sheetLlmId = (newSheet as any).llmId;
            
            log.log('[sheets_create] ✅ Spreadsheet created:', { 
                entityId, 
                sheetLlmId,
                name: newSheet.name 
            });
            
            // ✅ IMPORTANT: Register with dataspace tree if we have folderId
            if (folderId && entityId) {
                try {
                    const { default: useDataspaceTreeStore } = await import('../../../stores/dataspaceTreeStore');
                    const treeStore = useDataspaceTreeStore.getState();
                    const edgeKey = `${folderId}:${entityId}`;
                    
                    treeStore._applyAddNode(edgeKey, {
                        id: entityId,
                        parentId: folderId,
                        pos: "M",
                        expanded: false,
                        kind: 'canonical'
                    });
                    
                    log.log('[sheets_create] ✅ Added to dataspace tree:', { folderId, entityId });
                } catch (treeError) {
                    log.warn('[sheets_create] ⚠️ Failed to add to tree (non-fatal):', treeError);
                }
            }
            
            let windowInfo = null;
            
            // Create window only if requested (default: true for backwards compatibility)
            if (createWindow !== false && spaceId) {
                try {
                    // Use space_open_window command for proper multi-store coordination
                    const { useCommandRegistryStore } = await import('../../../stores/commandRegistryStore');
                    const { executeCommand } = useCommandRegistryStore.getState();
                    
                    console.log('[sheets_create] Opening window for entity:', {
                        entityId,
                        sheetLlmId,
                        entityType: newSheet.entityType,
                        name: newSheet.name
                    });
                    
                    const result = (await executeCommand('space_open_window', {
                        entityId
                    })) as import('../../../types/standardToolResults').StandardToolResult;
                    
                    if (result.status !== 'success') {
                        log.error('[sheets_create] Failed to open window:', result.message);
                        throw new Error(result.message || 'Failed to open window');
                    }
                    
                    const createdWindowId = result.data?.windowId;
                    
                    windowInfo = {
                        id: createdWindowId,
                        title: name.trim(),
                        appType: 'sheets-v2',
                        entityId,
                        entityLlmId: sheetLlmId
                    };
                    
                    log.log('[sheets_create] ✅ Window created:', { windowId: createdWindowId });
                } catch (windowError) {
                    log.error('[sheets_create] ❌ Failed to create window:', windowError);
                    // Don't fail the entire command if window creation fails
                }
            }
            
            const duration = Date.now() - startTime;
            
            // ✅ STANDARDIZED: Return entityId at top level for sidebar extraction
            return createSuccessResult(
                `Created spreadsheet: ${name}`,
                {
                    entityId: entityId, // ✅ CRITICAL: Top-level entityId for sidebar
                    name: newSheet.name,
                    entityType: 'Sheet',
                    sheet: {
                        id: entityId,
                        llmId: sheetLlmId,
                        name: newSheet.name,
                        entityType: newSheet.entityType || 'Sheet',
                        created_at: newSheet.created_at,
                        icon: newSheet.icon || '📊'
                    },
                    window: windowInfo
                },
                {
                    format: 'json',
                    metadata: {
                        executionTime: duration,
                        method: 'self-contained-v2',
                        affectedEntities: [entityId]
                    }
                }
            );
            
        } catch (error) {
            const duration = Date.now() - startTime;
            log.error('[sheets_create] Failed to create spreadsheet:', error);
            
            return createErrorResult(
                'Failed to create spreadsheet',
                error instanceof Error ? error.message : 'Unknown error occurred during spreadsheet creation',
                'execution',
                {
                    metadata: {
                        executionTime: duration
                    }
                }
            );
        }
    }
};

// Register in entity creation registry
entityCreationRegistry.register({
    commandId: 'sheets_create',
    entityType: 'Sheet',
    displayName: 'Spreadsheet',
    icon: { type: 'emoji', emoji: '📊' },
    description: 'Create a spreadsheet with rows and columns',
    canCreateInFolder: true,
    openAfterCreate: true,
    category: 'document'
});

